import HyperDX from "@hyperdx/browser";

// HyperDX browser observability (logs, traces, errors, masked session replay).
//
// Auth / CORS model: the browser SDK does NOT carry the real ingestion API key.
// It posts OTLP to the SAME ORIGIN as the app, so the SDK's `url` base is the
// page origin and it appends "/v1/traces" and "/v1/logs". nginx proxies "/v1/"
// to the self-hosted HyperDX collector and injects the real `Authorization`
// header server-side. This keeps the key out of the bundle and avoids CORS.
//
// Env vars (CRA -> must be prefixed REACT_APP_):
//   REACT_APP_HYPERDX_URL      - OTLP base origin (default: page origin -> "/v1/traces", "/v1/logs").
//   REACT_APP_HYPERDX_SERVICE  - service name shown in HyperDX (default "one-journey-frontend").
//   REACT_APP_HYPERDX_ENABLED  - set to "false" to disable (default enabled).
//
// PII NOTE: this is a KYC flow that captures identity documents, faces and PAN
// numbers. Session replay is enabled but ALL inputs and text are masked so the
// captured PII is redacted in recordings. Do not relax maskAllInputs /
// maskAllText without a privacy review.

// Non-empty placeholder so the SDK initializes normally; the REAL Authorization
// header is set by nginx on the proxied request, overriding this value.
const PROXY_PLACEHOLDER_KEY = "set-by-nginx-proxy";

let initialized = false;

// Build trace-propagation matchers from the backend base URL so frontend spans
// link to backend traces. Falls back to a permissive matcher if unset.
const buildTracePropagationTargets = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  if (!baseUrl) return [/.+/i];
  try {
    const host = new URL(baseUrl).host;
    const escaped = host.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return [new RegExp(escaped, "i")];
  } catch (e) {
    return [/.+/i];
  }
};

export const initHyperDX = () => {
  if (initialized) return;

  // Never run telemetry (and its console/network patching) in unit tests.
  if (process.env.NODE_ENV === "test") return;
  if (process.env.REACT_APP_HYPERDX_ENABLED === "false") return;
  if (typeof window === "undefined") return;

  try {
    HyperDX.init({
      apiKey: PROXY_PLACEHOLDER_KEY,
      service: process.env.REACT_APP_HYPERDX_SERVICE || "one-journey-frontend",
      // Same-origin base; SDK posts to "<origin>/v1/traces" and "<origin>/v1/logs",
      // which nginx proxies to the collector with the auth header injected.
      url: process.env.REACT_APP_HYPERDX_URL || window.location.origin,
      tracePropagationTargets: buildTracePropagationTargets(),
      consoleCapture: true,
      advancedNetworkCapture: true,
      // Redact PII in session replay (KYC documents / faces / PAN).
      maskAllInputs: true,
      maskAllText: true,
    });
    initialized = true;
  } catch (e) {
    // Never let observability setup break the app.
    console.error("[HyperDX] init failed:", e);
  }
};

// Attach identifiers / segmentation attributes (journey id, device, region,
// flow type, language, ...) to all subsequent telemetry. Safe no-op until
// HyperDX has been initialized. Undefined/null values are dropped so partial
// context can be set incrementally.
export const setHyperDXContext = (attributes = {}) => {
  if (!initialized) return;
  try {
    const cleaned = {};
    Object.entries(attributes).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") cleaned[String(k)] = String(v);
    });
    if (Object.keys(cleaned).length) HyperDX.setGlobalAttributes(cleaned);
  } catch (e) {
    console.error("[HyperDX] setGlobalAttributes failed:", e);
  }
};

// Emit a structured behavioral event (journey step, capture method, retake,
// permission grant/deny, terminal outcome, ...). These power funnels and
// drop-off analysis. Safe no-op until HyperDX has been initialized.
export const trackAction = (name, attributes = {}) => {
  if (!initialized || !name) return;
  try {
    const cleaned = {};
    Object.entries(attributes).forEach(([k, v]) => {
      if (v !== undefined && v !== null) cleaned[String(k)] = v;
    });
    HyperDX.addAction(name, cleaned);
  } catch (e) {
    console.error("[HyperDX] addAction failed:", e);
  }
};

export default HyperDX;
